import { cn } from '../../utils/cn';
import Skeleton from './Skeleton';

/**
 * Table — Zuzzuu 2.0 tablolarıyla uyumlu stil.
 * columns: [{ key, header, render?, className?, headerClassName? }]
 * rows: [{ id, ... }]
 *
 * loading=true iken `skeletonRows` kadar (default 6) pulse satırı render eder —
 * gerçek tablo yüksekliğini korur ve "Yükleniyor…" metnine göre daha lazy-load
 * hissi verir. Tablo sütun sayısına göre her satıra birer skeleton hücresi
 * konur.
 */
export default function Table({
  columns,
  rows,
  getRowKey = (r) => r.id,
  onRowClick,
  emptyMessage = 'Kayıt bulunamadı',
  loading = false,
  skeletonRows = 6,
  className,
  rowClassName,
  highlightRow,
}) {
  // Shared Zuzzuu 2.0 tablo stili (SMS/Push/Email app'lerindeki MRT tablosu
  // ile hizalanmış):
  //   - Başlık: siyah metin, font-weight 500, 16 px (uppercase yok)
  //   - Gövde: siyah-ish metin, 14 px, font-weight 400
  //   - Üst kenarlık/sütun ayracı soft gri (#E8E8E8 ≈ neutral-200)
  return (
    // Table artık kendi dış kenarlığını çizmiyor — her liste sayfası tabloyu
    // customers GridTable kabuğuyla hizalı bir `Card` / rounded-[1.25rem]
    // konteyner içine koyuyor. Böylece iç içe iki yuvarlak kutu olmuyor ve
    // toolbar/table/pagination tek bir beyaz kart hissi veriyor.
    <div className={cn('overflow-x-auto bg-white', className)}>
      <table className="min-w-full text-sm">
        <thead className="bg-white text-neutral-900">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'px-4 py-3 text-left text-base font-medium border-b border-neutral-200',
                  c.headerClassName,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200">
          {loading &&
            Array.from({ length: skeletonRows }).map((_, r) => (
              <tr key={`sk-${r}`} aria-hidden="true">
                {columns.map((c, ci) => (
                  <td
                    key={c.key || ci}
                    className={cn('px-4 py-3 align-middle', c.className)}
                  >
                    <Skeleton
                      className={cn(
                        'h-3.5',
                        // Sütuna göre farklı genişlikler — tablo daha doğal görünsün
                        ci === 0 ? 'w-3/4' : ci === columns.length - 1 ? 'w-1/3' : 'w-1/2',
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-neutral-400">
                {emptyMessage}
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((row, idx) => (
              <tr
                key={getRowKey(row) ?? idx}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  // email_app EmailGridTable hover rengiyle hizalı (#f9fafb)
                  'transition-colors hover:bg-neutral-50',
                  onRowClick && 'cursor-pointer',
                  highlightRow && highlightRow(row) && 'bg-amber-50 hover:bg-amber-100',
                  typeof rowClassName === 'function' ? rowClassName(row, idx) : rowClassName,
                )}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      'px-4 py-3 text-sm font-normal text-neutral-900 align-middle',
                      c.className,
                    )}
                  >
                    {c.render ? c.render(row, idx) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
